import { useEffect, useMemo, useState } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded'
import NotesRoundedIcon from '@mui/icons-material/NotesRounded'
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded'
import TodayRoundedIcon from '@mui/icons-material/TodayRounded'
import { DateStrip } from './DateStrip'
import { useCalendarEvents } from './useCalendarEvents'
import { addDays, startOfWeek, toIsoDate, type CalendarEvent } from './calendarHelpers'
import { createMeetingProviderAdapter, subscribeMeetingMembers, subscribeMeetings, type MeetingMember } from '../../services/meetingService'
import { subscribeHuddleSessions, type HuddleSession } from '../../services/huddleSessionService'
import { usePersistedState } from '../../hooks/usePersistedState'
import { useAuth } from '../../context/useAuth'
import { useTimeline } from '../../context/useTimeline'
import { useToast } from '../../context/useToast'
import { type CalendarBoardFilterState, type CalendarRangeMode } from '../../types/schema'
import type { MeetingAgendaNode, MeetingProvider, MeetingRecord } from '../../types/meetings'
import { useNls } from '../../nls/nlsProvider'
import { MemberActionCard, MemberBottomSheet, MemberEmptyState, MemberSegmented } from '../Member/controls/MemberControlsPrimitives'
import { MemberNexusHero, MemberSurfaceFrame, type MemberNexusChip } from '../Member/controls/MemberNexusPrimitives'
import { FloatingInput } from '../Common/FloatingInput'
import { SheetDateField } from '../Common/SheetDateField'
import { SheetSelect } from '../Common/SheetSelect'
import { LinkedGoalPicker, TextInput, Textarea, useLinkedGoalPrefill } from '../Common'
import { Button } from '../Common/Button'
import { buildMemberMeetingCreateInput, dispatchMeetingPaywall } from '../Member/nexus/memberMeetingDraft'

type Props = {
  workspaceId: string
  readOnly?: boolean
  canScheduleMeetings?: boolean
  workspaceOwner?: { id: string; name: string }
  personalWorkspace?: boolean
  nexusChips?: MemberNexusChip[]
  onOpenElement?: (elementId: string) => void
  onOpenChecklistItem?: (parentElementId: string, checklistItemId: string) => void
  onOpenChat?: () => void
  onOpenMeetings?: () => void
  onOpenHuddle?: () => void
}

const filterState: CalendarBoardFilterState = { explicit: {}, showAll: true }

export const MemberCalendarScreen = ({
  workspaceId,
  readOnly = false,
  canScheduleMeetings = !readOnly,
  workspaceOwner,
  personalWorkspace = false,
  nexusChips = [],
  onOpenElement,
  onOpenChecklistItem,
  onOpenChat,
  onOpenMeetings,
  onOpenHuddle,
}: Props) => {
  const { elements, isAccessRestricted } = useTimeline()
  const { user } = useAuth()
  const { showToast } = useToast()
  const { t } = useNls()
  const [mode, setMode] = usePersistedState<CalendarRangeMode>('member.calendarMode', 'day')
  const [focusedDate, setFocusedDate] = useState(() => new Date())
  const [meetings, setMeetings] = useState<MeetingRecord[]>([])
  const [huddleSessions, setHuddleSessions] = useState<HuddleSession[]>([])
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [draftTitle, setDraftTitle] = useState('')
  const [draftNotes, setDraftNotes] = useState('')
  const [draftDate, setDraftDate] = useState(() => toIsoDate(new Date()))
  const [draftTime, setDraftTime] = useState('09:00')
  const [draftProvider, setDraftProvider] = useState<MeetingProvider>('GOOGLE_MEET')
  const [draftDuration, setDraftDuration] = useState('30')
  const prefillGoalId = useLinkedGoalPrefill()
  const [draftAttachedElementId, setDraftAttachedElementId] = useState<string | null>(null)
  const [draftScopeNodeId, setDraftScopeNodeId] = useState<string | null>(prefillGoalId)
  const [members, setMembers] = useState<MeetingMember[]>([])
  const [participantIds, setParticipantIds] = useState<string[]>([])
  const [participantChoice, setParticipantChoice] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const uid = user?.uid ?? ''

  useEffect(() => subscribeMeetings(workspaceId, setMeetings, () => setMeetings([]), { restricted: !!isAccessRestricted, uid }), [workspaceId, isAccessRestricted, uid])
  useEffect(() => subscribeHuddleSessions(workspaceId, setHuddleSessions, () => setHuddleSessions([]), { restricted: !!isAccessRestricted, uid }), [workspaceId, isAccessRestricted, uid])
  useEffect(() => workspaceOwner
    ? subscribeMeetingMembers(workspaceId, workspaceOwner, personalWorkspace, setMembers, () => setMembers([]))
    : undefined, [personalWorkspace, workspaceId, workspaceOwner])

  const windowStart = mode === 'month'
    ? new Date(focusedDate.getFullYear(), focusedDate.getMonth(), 1)
    : mode === 'week'
      ? startOfWeek(focusedDate)
      : focusedDate
  const windowEnd = mode === 'month'
    ? new Date(focusedDate.getFullYear(), focusedDate.getMonth() + 1, 0)
    : mode === 'week'
      ? addDays(startOfWeek(focusedDate), 6)
      : focusedDate

  const events = useCalendarEvents({
    meetings,
    huddleSessions,
    activeBoard: 'DASHBOARD',
    boardScope: { mode: 'custom', boardKeys: [] },
    filterState,
    windowStartIso: toIsoDate(windowStart),
    windowEndIso: toIsoDate(windowEnd),
  })

  const focusedIso = toIsoDate(focusedDate)
  const dayEvents = useMemo(
    () => events.all.filter((event) => event.startDate <= focusedIso && event.endDate >= focusedIso),
    [events.all, focusedIso],
  )
  const todayIso = toIsoDate(new Date())
  const todayCount = events.all.filter((event) => event.startDate <= todayIso && event.endDate >= todayIso).length

  const title = focusedDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
  const huddleCount = events.all.filter((event) => event.kind === 'huddle').length
  const chips: MemberNexusChip[] = [
    { key: 'calendar-today', label: t('NLSMemberCalendarToday'), value: todayCount, icon: <TodayRoundedIcon fontSize="inherit" />, onClick: () => setFocusedDate(new Date()) },
    { key: 'calendar-huddles', label: t('NLSMemberCalendarHuddles'), value: huddleCount, icon: <CalendarMonthRoundedIcon fontSize="inherit" /> },
    ...nexusChips,
  ]

  const eventStatus = (event: CalendarEvent) => event.kind === 'huddle' && event.isOngoing
    ? t('NLSMemberCalendarOngoing')
    : event.endDate < toIsoDate(new Date()) ? t('NLSMemberCalendarDone') : t('NLSMemberCalendarConfirmed')
  const eventSubline = (event: CalendarEvent) => {
    const time = event.startTime && event.endTime ? `${event.startTime} - ${event.endTime}` : t('NLSMemberCalendarAllDay')
    const source = event.kind === 'huddle' ? t('NLSMemberCalendarHuddle') : t('NLSMemberCalendarMeeting')
    return `${time} · ${source}`
  }
  const eventTone = (event: CalendarEvent) => {
    if (event.kind === 'huddle' && event.isOngoing) return 'success' as const
    if (event.endDate < toIsoDate(new Date())) return 'success' as const
    return event.kind === 'meeting' ? 'info' as const : 'neutral' as const
  }
  const openEventTarget = (event: CalendarEvent) => {
    if (event.kind === 'huddle') onOpenHuddle?.()
    else if (event.elementId) onOpenElement?.(event.elementId)
    else if (event.parentElementId && event.checklistItemId) onOpenChecklistItem?.(event.parentElementId, event.checklistItemId)
    else setSelectedEvent(event)
  }

  const resetDraft = () => {
    setDraftTitle(''); setDraftNotes(''); setDraftDate(toIsoDate(focusedDate)); setDraftTime('09:00'); setDraftProvider('GOOGLE_MEET'); setDraftDuration('30'); setDraftAttachedElementId(null); setDraftScopeNodeId(prefillGoalId); setParticipantIds([]); setParticipantChoice(null)
  }
  const addParticipant = (uid: string) => {
    setParticipantIds((current) => current.includes(uid) ? current : [...current, uid])
    setParticipantChoice(null)
  }
  const submitDraft = async () => {
    if (!canScheduleMeetings) return
    const attachedElement = draftAttachedElementId ? elements.find((element) => element.id === draftAttachedElementId) : undefined
    const agendaNodes: MeetingAgendaNode[] = attachedElement ? [{ nodeId: attachedElement.id, nodeType: 'ELEMENT', title: attachedElement.title }] : []
    const input = buildMemberMeetingCreateInput({ workspaceId, title: draftTitle, sourceProvider: draftProvider, date: draftDate, time: draftTime, durationMinutes: Number(draftDuration), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, agendaMarkdown: draftNotes, agendaNodes, participantIds, scopeNodeId: draftScopeNodeId })
    if (!input) return
    setSaving(true)
    try {
      await createMeetingProviderAdapter(draftProvider).createMeeting(input)
      setAddOpen(false)
      resetDraft()
      showToast(t('NLSMemberCalendarCreated'), 'success')
    } catch (error) {
      if (!dispatchMeetingPaywall(workspaceId, error)) showToast(error instanceof Error ? error.message : t('NLSMemberCalendarCreateFailed'), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <MemberSurfaceFrame
      className="memberCalendarMobile"
      testId="member-calendar-screen"
      bottomSlot={canScheduleMeetings && dayEvents.length > 0 ? (
        <Button
          variant="primary"
          icon={<AddRoundedIcon fontSize="small" />}
          onClick={() => {
            setDraftDate(focusedIso)
            setDraftAttachedElementId(null)
            setAddOpen(true)
          }}
        >
          {t('NLSMemberCalendarAdd')}
        </Button>
      ) : undefined}
    >
      <MemberNexusHero eyebrow={t('NLSMemberSubtabCalendar')} title={title} chips={chips} />
      <MemberSegmented
        value={mode}
        onChange={setMode}
        ariaLabel={t('NLSMemberCalendarModeAria')}
        options={[
          { key: 'day', label: t('NLSMemberCalendarDay') },
          { key: 'week', label: t('NLSMemberCalendarWeek') },
          { key: 'month', label: t('NLSMemberCalendarMonth') },
        ]}
      />
      <div className="memberCalendarStrip">
        <DateStrip focusedDate={focusedDate} events={events.all} onDateClick={setFocusedDate} range={mode} />
      </div>
      <section className="memberNexusSection">
        <div className="memberNexusSection__header">
          <span>{focusedDate.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}</span>
          <span>{dayEvents.length}</span>
        </div>
        {dayEvents.length > 0 ? (
          <div className="memberNexusCardList">
            {dayEvents.map((event) => (
              <MemberActionCard
                key={event.id}
                icon={event.kind === 'huddle' ? <SensorsRoundedIcon /> : <EventAvailableRoundedIcon />}
                title={event.title}
                sub={eventSubline(event)}
                status={eventStatus(event)}
                statusTone={eventTone(event)}
                onClick={() => openEventTarget(event)}
              />
            ))}
          </div>
        ) : (
          <MemberEmptyState
            icon={<CalendarMonthRoundedIcon />}
            title={t('NLSMemberCalendarEmptyTitle')}
            hint={canScheduleMeetings ? t('NLSMemberCalendarEmptyHint') : t('NLSMemberCalendarEmptyReadOnlyHint')}
            cta={canScheduleMeetings ? (
              <Button variant="primary" onClick={() => { setDraftDate(focusedIso); setDraftAttachedElementId(null); setAddOpen(true) }}>
                {t('NLSMemberCalendarAddEvent')}
              </Button>
            ) : undefined}
          />
        )}
      </section>

      <MemberBottomSheet open={Boolean(selectedEvent)} onClose={() => setSelectedEvent(null)} title={selectedEvent?.title ?? t('NLSMemberCalendarEvent')}>
        {selectedEvent ? (
          <div className="memberNexusSheet">
            <MemberActionCard
              icon={<NotesRoundedIcon />}
              title={selectedEvent.title}
              sub={eventSubline(selectedEvent)}
              status={eventStatus(selectedEvent)}
              statusTone={eventTone(selectedEvent)}
            />
            <div className="memberNexusSheet__actions">
              <Button variant="tertiary" size="sm" onClick={() => showToast(t('NLSMemberCalendarTodoQueued'), 'ghost')}>{t('NLSMemberCalendarAddToTodos')}</Button>
              <Button variant="tertiary" size="sm" onClick={onOpenChat}>{t('NLSMemberCalendarMessageChannel')}</Button>
              <Button variant="tertiary" size="sm" onClick={onOpenMeetings}>{t('NLSMemberSubtabMeetings')}</Button>
            </div>
          </div>
        ) : null}
      </MemberBottomSheet>

      <MemberBottomSheet open={addOpen} onClose={() => setAddOpen(false)} title={t('NLSMemberCalendarSchedule')}>
        <div className="memberNexusSheet memberCalendarDraftSheet">
          <label>
            <span>{t('NLSMemberCalendarTitle')}</span>
            <FloatingInput label={t('NLSMemberCalendarTitle')}>
              <TextInput className="memberTextOverlayField" value={draftTitle} onChange={setDraftTitle} placeholder={t('NLSMemberCalendarTitlePlaceholder')} />
            </FloatingInput>
          </label>
          <label><span>{t('NLSMemberCalendarType')}</span><SheetSelect ariaLabel={t('NLSMemberCalendarType')} value={draftProvider} options={[{ value: 'GOOGLE_MEET', label: t('NLSMemberCalendarTypeMeeting') }, { value: 'HUDDLE', label: t('NLSMemberCalendarTypeHuddle') }]} onChange={setDraftProvider} /></label>
          <label><span>{t('NLSMemberCalendarDate')}</span><SheetDateField ariaLabel={t('NLSMemberCalendarDate')} value={draftDate} type="date" placeholder={t('NLSMemberCalendarDate')} onChange={setDraftDate} /></label>
          <label><span>{t('NLSMemberCalendarTime')}</span><SheetDateField ariaLabel={t('NLSMemberCalendarTime')} value={draftTime} type="time" placeholder={t('NLSMemberCalendarTime')} onChange={setDraftTime} /></label>
          <label><span>{t('NLSMemberCalendarDuration')}</span><SheetSelect ariaLabel={t('NLSMemberCalendarDuration')} value={draftDuration} options={['15', '30', '45', '60', '90', '120', '10080'].map((value) => ({ value, label: t('NLSMemberCalendarDurationMinutes', { minutes: Number(value) }) }))} onChange={setDraftDuration} /></label>
          {members.length > 0 ? <label><span>{t('NLSMemberCalendarParticipants')}</span><SheetSelect ariaLabel={t('NLSMemberCalendarParticipants')} value={participantChoice} placeholder={t('NLSMemberCalendarAddParticipant')} options={members.filter((member) => !participantIds.includes(member.uid)).map((member) => ({ value: member.uid, label: member.name }))} onChange={addParticipant} /></label> : null}
          {participantIds.length > 0 ? <div className="memberNexusSheet__actions">{participantIds.map((uid) => <Button key={uid} variant="tertiary" size="sm" tone="danger" onClick={() => setParticipantIds((current) => current.filter((id) => id !== uid))}>{t('NLSMemberCalendarRemoveParticipant', { name: members.find((member) => member.uid === uid)?.name ?? uid })}</Button>)}</div> : null}
          <label>
            <span>{t('NLSMemberCalendarNotes')}</span>
            <FloatingInput label={t('NLSMemberCalendarNotes')}>
              <Textarea className="memberTextOverlayField memberCalendarDraftSheet__notes" value={draftNotes} onChange={setDraftNotes} placeholder={t('NLSMemberCalendarNotesPlaceholder')} />
            </FloatingInput>
          </label>
          <label><span>{t('NLSMemberCalendarAttachTask')}</span><SheetSelect ariaLabel={t('NLSMemberCalendarAttachTask')} value={draftAttachedElementId} placeholder={t('NLSMemberCalendarNone')} options={elements.slice(0, 20).map((element) => ({ value: element.id, label: element.title }))} onChange={setDraftAttachedElementId} /></label>
          <LinkedGoalPicker value={draftScopeNodeId} onChange={setDraftScopeNodeId} disabled={saving} />
          <Button variant="primary" disabled={!draftTitle.trim() || saving} loading={saving} onClick={() => void submitDraft()}>
            {saving ? t('NLSMemberCalendarCreating') : t('NLSMemberCalendarAdd')}
          </Button>
        </div>
      </MemberBottomSheet>
    </MemberSurfaceFrame>
  )
}
